import React from 'react';
import { useRouteMatch } from 'react-router-dom';
import { differenceInDays } from 'date-fns';
import { parseISODate } from '@ygtechnologies/rubus-frontend-utilities';
import { Accordion } from '@ygtechnologies/rubus-ui';

import { BuyerProjectDeadline } from '@/types';

import { useScrollIntoView } from '../shared/useScrollIntoView';
import { useOptionsScope } from '../OptionsScope';
import type { RoomGroup } from '../OptionsPage';
import { getGroupStatus } from '../shared/groupStatus';
import { OptionRow } from '../optionRow/OptionRow';
import { DeadlineHeader } from './DeadlineHeader';
import { SubmitPrompt } from './SubmitPrompt';
import theme from './DeadlineBlock.module.scss';

interface Props {
    deadline: BuyerProjectDeadline;
    groupsForDeadline: RoomGroup[];
    /** Unfiltered list for this deadline — used for the unsigned-count banner. */
    unfilteredForDeadline: RoomGroup[];
    shouldScrollTo: boolean;
    onSelectionChanged?: () => void;
}

export const DeadlineBlock: React.FC<Props> = ({
    deadline,
    groupsForDeadline,
    unfilteredForDeadline,
    shouldScrollTo,
    onSelectionChanged,
}) => {
    const containerRef = useScrollIntoView<HTMLDivElement>(shouldScrollTo);
    const match = useRouteMatch();
    const { showSubmit, mode, readOnly } = useOptionsScope();
    const submitUrl = `${match?.url?.replace(/\/$/, '') ?? ''}/submit/${deadline.id}`;

    if (groupsForDeadline.length === 0) return null;

    const daysRemaining = differenceInDays(parseISODate(deadline.deadline), new Date());
    const expired = daysRemaining < 0;
    const canChangeAny = groupsForDeadline.some(({ group }) => !group.isLocked);

    // Who may sign, and when:
    // - showSubmit: signing exists at all (false in the showroom).
    // - !readOnly: a seller impersonating a buyer may not sign.
    // - a seller/admin may sign after the deadline; a buyer only before it expires.
    // - canChangeAny: at least one group is still open to change.
    // canSign gates both the sign button and the SubmitPrompt.
    const deadlineAllowsSigning = mode === 'seller' || !expired;
    const canSign = showSubmit && !readOnly && deadlineAllowsSigning && canChangeAny;
    const unsignedCount = showSubmit
        ? unfilteredForDeadline.filter(({ group }) => getGroupStatus(group) !== 'Sent').length
        : 0;

    return (
        <div ref={containerRef}>
            <Accordion
                stickyHeader
                defaultOpen
                variant="elevated"
                title={
                    <DeadlineHeader
                        deadline={deadline}
                        unsignedCount={unsignedCount}
                        canSign={canSign}
                        submitUrl={submitUrl}
                    />
                }>
                <div className={theme.optionList}>
                    {groupsForDeadline.map(({ room, group }) => (
                        <OptionRow
                            key={`${group.id}:${room.roomId}`}
                            room={room}
                            group={group}
                            deadlineDate={deadline.deadline}
                            onSelectionChanged={onSelectionChanged}
                        />
                    ))}
                    {canSign && (
                        <SubmitPrompt
                            deadline={deadline}
                            pendingCount={groupsForDeadline.length}
                            submitUrl={submitUrl}
                        />
                    )}
                </div>
            </Accordion>
        </div>
    );
};
